import React from 'react';
import { Box, Button, Typography, Alert, Stack, Link } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

interface Props {
  children: React.ReactNode;
  /** "global" = full-page crash screen, "section" = compact inline fallback */
  variant?: 'global' | 'section';
  /** Shown in the section fallback header, e.g. "Agent Workspace" */
  sectionName?: string;
  /** Optional controlled reset callback (section variant) */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
    console.error(
      `[ErrorBoundary:${this.props.variant ?? 'section'}]`,
      error,
      errorInfo.componentStack,
    );
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleDashboard = (): void => {
    window.location.href = '/dashboard';
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // ── Global variant: full-page crash screen ──────────────────────
    if (this.props.variant === 'global') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 4,
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Something went wrong
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 480, textAlign: 'center' }}
          >
            An unexpected error occurred. You can try reloading the page or
            return to the dashboard.
          </Typography>
          {this.state.error && (
            <Alert severity="error" sx={{ mb: 3, maxWidth: 600, width: '100%' }}>
              {this.state.error.message}
            </Alert>
          )}
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
            >
              Reload Page
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={this.handleDashboard}
            >
              Back to Dashboard
            </Button>
          </Stack>
        </Box>
      );
    }

    // ── Section variant: compact inline fallback ────────────────────
    const label = this.props.sectionName
      ? `${this.props.sectionName} encountered an error`
      : 'This section encountered an error';

    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={this.handleReset}>
              Retry
            </Button>
          }
        >
          <Typography variant="subtitle2">{label}</Typography>
          {this.state.error && (
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', mt: 0.5 }}
            >
              {this.state.error.message}
            </Typography>
          )}
        </Alert>
        <Link
          component="button"
          variant="body2"
          onClick={this.handleDashboard}
          sx={{ cursor: 'pointer' }}
        >
          Back to Dashboard
        </Link>
      </Box>
    );
  }
}

export default ErrorBoundary;
